import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { User as UserIcon, Mail, Phone, Lock, Bell, Trash2, Check, Eye, EyeOff, AlertTriangle } from "lucide-react";

const Section = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-zinc-200 p-6">
    <div className="mb-5">
      <h2 className="font-bold text-zinc-900">{title}</h2>
      {desc && <p className="text-sm text-zinc-500 mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);

const Settings = () => {
  const { user, updateProfile, signOut } = useAuth();
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [savedProfile, setSavedProfile] = useState(false);

  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [pwd3, setPwd3] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const [prefs, setPrefs] = useState({
    promo: true,
    orderUpdates: true,
    designTips: false,
    sms: false,
  });

  const saveProfile = (e) => {
    e.preventDefault();
    updateProfile({ firstName, lastName, email, phone });
    setSavedProfile(true);
    setTimeout(() => setSavedProfile(false), 2000);
  };

  const savePwd = (e) => {
    e.preventDefault();
    if (pwd2 !== pwd3) return alert("New passwords don't match");
    if (pwd2.length < 6) return alert("New password must be 6+ characters");
    alert("Password updated.");
    setPwd1(""); setPwd2(""); setPwd3("");
  };

  const deleteAccount = () => {
    if (window.confirm("Are you sure? This will sign you out and clear your local session.")) {
      signOut();
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h1 className="text-2xl font-bold text-zinc-900">Account Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Update your personal info, password and notification preferences.</p>
      </div>

      <Section title="Personal information" desc="This is the name and contact info on your invoices.">
        <form onSubmit={saveProfile} className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-zinc-700">
            First name
            <div className="relative mt-1">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" />
            </div>
          </label>
          <label className="text-xs font-semibold text-zinc-700">
            Last name
            <div className="relative mt-1">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" />
            </div>
          </label>
          <label className="text-xs font-semibold text-zinc-700 sm:col-span-1">
            Email
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" />
            </div>
          </label>
          <label className="text-xs font-semibold text-zinc-700">
            Phone
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" />
            </div>
          </label>
          <div className="sm:col-span-2 flex items-center gap-3 pt-2">
            <button className="bg-zinc-900 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors inline-flex items-center gap-2">
              <Check className="w-4 h-4" /> Save changes
            </button>
            {savedProfile && <span className="text-xs text-emerald-600 font-semibold">Saved.</span>}
          </div>
        </form>
      </Section>

      <Section title="Change password" desc="Use 8+ characters with letters and numbers for the strongest security.">
        <form onSubmit={savePwd} className="space-y-3 max-w-md">
          {[
            { v: pwd1, set: setPwd1, ph: "Current password" },
            { v: pwd2, set: setPwd2, ph: "New password" },
            { v: pwd3, set: setPwd3, ph: "Confirm new password" },
          ].map((f, i) => (
            <div key={i} className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type={showPwd ? "text" : "password"}
                value={f.v}
                onChange={(e) => f.set(e.target.value)}
                placeholder={f.ph}
                required
                className="w-full pl-9 pr-9 py-2.5 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400"
              />
              {i === 0 && (
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
          <button className="bg-zinc-900 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors">Update password</button>
        </form>
      </Section>

      <Section title="Notifications" desc="Choose what we send and how we send it.">
        <ul className="space-y-3">
          {[
            { k: "orderUpdates", t: "Order updates", d: "Production milestones, shipping & delivery emails." },
            { k: "promo", t: "Monthly promo codes", d: "We email new promo codes once a month — no spam." },
            { k: "designTips", t: "Design tips & inspiration", d: "Quick weekly read on print, branding and color." },
            { k: "sms", t: "SMS notifications", d: "Text alerts for delivery only. Carrier rates may apply." },
          ].map((p) => (
            <li key={p.k} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-zinc-100 hover:border-zinc-200 transition-colors">
              <div className="flex gap-3">
                <Bell className="w-4 h-4 text-zinc-400 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-zinc-900">{p.t}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.d}</div>
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer shrink-0">
                <input type="checkbox" className="sr-only peer" checked={prefs[p.k]} onChange={(e) => setPrefs((s) => ({ ...s, [p.k]: e.target.checked }))} />
                <span className="w-11 h-6 bg-zinc-200 rounded-full peer-checked:bg-rose-500 transition-colors relative">
                  <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Danger zone" desc="Deleting your account removes saved designs, addresses and order history.">
        <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-zinc-900">Delete this account</div>
              <div className="text-xs text-zinc-600 mt-1">This is permanent — we can't restore your data once deleted.</div>
            </div>
          </div>
          <button onClick={deleteAccount} className="bg-white border border-rose-300 text-rose-600 hover:bg-rose-500 hover:text-white px-4 py-2.5 rounded-lg text-xs font-bold transition-colors inline-flex items-center gap-2">
            <Trash2 className="w-3.5 h-3.5" /> Delete account
          </button>
        </div>
      </Section>
    </div>
  );
};

export default Settings;
